import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Card, TextField, MenuItem, Button, 
    Typography, Snackbar, Alert, Grid, Stepper, Step, StepLabel,
    Select, OutlinedInput, Chip, FormControl, InputLabel
} from "@mui/material";
import {
    UploadFile as UploadIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    Save as SaveIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { useNavigate } from 'react-router-dom';

const AdhocVariablesImport = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });

    // Step 1 State
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
    });
    const [payElements, setPayElements] = useState([]);
    const [selectedElementIds, setSelectedElementIds] = useState([]);
    const [uploadFile, setUploadFile] = useState(null);

    // Step 2 State
    const [previewData, setPreviewData] = useState(null);

    const steps = ['Upload Data', 'Preview Data', 'Confirm & Commit'];
    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];
    const years = [2025, 2026, 2027];

    useEffect(() => {
        fetchPayElements();
    }, []);

    const fetchPayElements = async () => {
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setPayElements(response.data.data.filter(e => e.is_active === 1));
            }
        } catch (error) {
            console.error("Error fetching elements", error);
        }
    };

    const handleDownloadTemplate = async () => {
        if (selectedElementIds.length === 0) {
            setStatusMsg({ open: true, msg: 'Please select at least one Pay Element', severity: 'warning' });
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/adhoc/template`, 
                { element_ids: selectedElementIds },
                {
                    headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                    withCredentials: true,
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'PeopleHub_Payroll_Import_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error downloading template", error);
            setStatusMsg({ open: true, msg: 'Error downloading template', severity: 'error' });
        }
    };

    const handlePreview = async () => {
        if (!uploadFile) {
            setStatusMsg({ open: true, msg: 'Please select an Excel file', severity: 'warning' });
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const formData = new FormData();
            formData.append('file', uploadFile);

            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/adhoc/preview`, formData, {
                headers: { 
                    Authorization: `Bearer ${token}`, 
                    'X-Tenant': tenant,
                    'Content-Type': 'multipart/form-data'
                },
                withCredentials: true
            });
            
            if (response.data.success) {
                setPreviewData(response.data.data);
                setActiveStep(1);
            }
        } catch (error) {
            console.error("Error generating preview", error);
            const msg = error.response?.data?.message || 'Error parsing file';
            setStatusMsg({ open: true, msg: msg, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCommit = async () => {
        if (!previewData) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const payload = {
                payroll_month: filters.month,
                payroll_year: filters.year,
                validElementIds: previewData.validElementIds,
                previewRows: previewData.previewRows
            };

            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/adhoc/commit`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            
            if (response.data.success) {
                setStatusMsg({ open: true, msg: response.data.message, severity: 'success' });
                setActiveStep(2);
            }
        } catch (error) {
            console.error("Error committing data", error);
            const msg = error.response?.data?.message || 'Error saving records';
            setStatusMsg({ open: true, msg: msg, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const previewColumnDefs = useMemo(() => {
        if (!previewData || !previewData.previewRows || previewData.previewRows.length === 0) return [];

        const baseCols = [
            { field: "row_index", headerName: "Row", width: 80, pinned: 'left' },
            { 
                field: "status", 
                headerName: "Status", 
                width: 130, 
                pinned: 'left',
                cellRenderer: p => (
                    <Chip 
                        label={p.value} 
                        color={p.value === 'MATCHED' ? 'success' : 'error'} 
                        size="small" 
                        sx={{ fontWeight: 'bold' }}
                    />
                )
            },
            { field: "employee_number", headerName: "Emp. No", width: 120, pinned: 'left' },
            { field: "full_name", headerName: "Staff Name", width: 180, pinned: 'left' }
        ];

        // Dynamically add columns for elements
        const elementCols = new Set();
        previewData.previewRows.forEach(r => {
            Object.keys(r.elements).forEach(k => elementCols.add(k));
        });

        const dynamicCols = Array.from(elementCols).map(col => ({
            headerName: col,
            valueGetter: p => p.data.elements[col]?.amount || '',
            width: 130,
            valueFormatter: p => p.value ? p.value.toLocaleString() : '-',
            cellStyle: p => p.value ? { fontWeight: 'bold', color: '#1976d2' } : {}
        }));

        return [...baseCols, ...dynamicCols];
    }, [previewData]);

    const renderStepContent = () => {
        if (activeStep === 0) {
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 3, color: '#1a237e', fontWeight: 600 }}>1. Configuration</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Payroll Month"
                                        value={filters.month}
                                        onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                                    >
                                        {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Payroll Year"
                                        value={filters.year}
                                        onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                                    >
                                        {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Select Pay Elements to Import</InputLabel>
                                        <Select
                                            multiple
                                            value={selectedElementIds}
                                            onChange={(e) => setSelectedElementIds(e.target.value)}
                                            input={<OutlinedInput label="Select Pay Elements to Import" />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((value) => {
                                                        const el = payElements.find(e => e.id === value);
                                                        return <Chip key={value} label={el ? el.element_code : value} size="small" />;
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {payElements.map((el) => (
                                                <MenuItem key={el.id} value={el.id}>
                                                    {el.element_code} - {el.element_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button 
                                        variant="outlined" 
                                        fullWidth 
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadTemplate}
                                        disabled={selectedElementIds.length === 0}
                                        sx={{ py: 1.5, borderRadius: 2 }}
                                    >
                                        Download Template
                                    </Button>
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                                        Template columns will adjust based on selected elements.
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 3, color: '#1a237e', fontWeight: 600 }}>2. Upload File</Typography>
                            <Box 
                                sx={{ 
                                    border: '2px dashed #ccc', 
                                    borderRadius: 2, 
                                    p: 4, 
                                    textAlign: 'center',
                                    bgcolor: '#fafafa',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: '#f0f0f0' },
                                    height: 180,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                component="label"
                            >
                                <input
                                    type="file"
                                    accept=".xlsx, .xls"
                                    hidden
                                    onChange={(e) => setUploadFile(e.target.files[0])}
                                />
                                <UploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                                {uploadFile ? (
                                    <Typography variant="subtitle1" color="primary">{uploadFile.name}</Typography>
                                ) : (
                                    <Typography variant="subtitle1" color="textSecondary">Click to select filled Excel file</Typography>
                                )}
                            </Box>
                            <Box sx={{ mt: 3, textAlign: 'right' }}>
                                <Button 
                                    variant="contained" 
                                    color="primary"
                                    onClick={handlePreview}
                                    disabled={!uploadFile || loading}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{ borderRadius: 2, px: 4 }}
                                >
                                    Parse & Preview
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            );
        }

        if (activeStep === 1 && previewData) {
            return (
                <Card sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 600 }}>Preview Data</Typography>
                            <Typography variant="body2" color="textSecondary">
                                <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>{previewData.stats.totalMatched} Matched</span> | <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>{previewData.stats.totalNotFound} Not Found</span>
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button 
                                variant="outlined" 
                                onClick={() => setActiveStep(0)}
                                startIcon={<ArrowBackIcon />}
                            >
                                Back
                            </Button>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={handleCommit}
                                disabled={loading || previewData.stats.totalMatched === 0}
                                startIcon={<SaveIcon />}
                            >
                                Confirm & Import
                            </Button>
                        </Box>
                    </Box>

                    {previewData.stats.totalNotFound > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Some rows could not be matched by Employee Number. They will be skipped during import.
                        </Alert>
                    )}

                    <div className="ag-theme-quartz" style={{ height: 500, width: '100%' }}>
                        <AgGridReact
                            rowData={previewData.previewRows}
                            columnDefs={previewColumnDefs}
                            defaultColDef={{ resizable: true, sortable: true }}
                            animateRows={true}
                        />
                    </div>
                </Card>
            );
        }

        if (activeStep === 2) {
            return (
                <Card sx={{ p: 5, borderRadius: 2, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ fontSize: 80, color: '#2e7d32', mb: 2 }} />
                    <Typography variant="h4" sx={{ color: '#1a237e', fontWeight: 700, mb: 1 }}>Import Successful</Typography>
                    <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 4 }}>
                        The ad-hoc variables have been successfully saved for the selected month.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button 
                            variant="outlined" 
                            onClick={() => {
                                setActiveStep(0);
                                setPreviewData(null);
                                setUploadFile(null);
                            }}
                        >
                            Import Another File
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => navigate('/payroll/adhoc')}
                        >
                            View Ad-hoc Variables
                        </Button>
                        <Button 
                            variant="contained" 
                            color="secondary"
                            onClick={() => navigate('/payroll/processing')}
                        >
                            Go to Payroll
                        </Button>
                    </Box>
                </Card>
            );
        }

        return null;
    };

    return (
        <Box sx={{ p: 0, backgroundColor: '#f5f7fb', minHeight: '100vh' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', mb: 3 }}>
                    Import Ad-Hoc Variables
                </Typography>

                <Card sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Card>

                {renderStepContent()}
            </Box>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={4000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={statusMsg.severity} variant="filled">
                    {statusMsg.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdhocVariablesImport;
